import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { loadRazorpay } from "@/lib/razorpay";
import { Logo, rupee } from "@/components/Bits";
import { toast } from "sonner";
import { ArrowLeft, QrCode, CreditCard, Wallet, Landmark, Clock3, Smartphone, ShieldCheck, CheckCircle2, Download } from "lucide-react";

const METHODS = [
  { key: "upi", label: "UPI", sub: "Google Pay, PhonePe, Paytm & more", icon: Smartphone },
  { key: "card", label: "Credit / Debit Cards", sub: "Mastercard, Visa, RuPay & more", icon: CreditCard, badge: "4 Offers" },
  { key: "paylater", label: "Pay Later", sub: "LazyPay, Simpl", icon: Clock3 },
  { key: "wallet", label: "Wallets", sub: "Amazon Pay, PhonePe, Mobikwik", icon: Wallet },
  { key: "netbanking", label: "Net Banking", sub: "HDFC, SBI, ICICI, AXIS, Kotak & more", icon: Landmark },
];
const BANKS = ["HDFC Bank", "State Bank of India", "ICICI Bank", "Axis Bank", "Kotak Mahindra", "Punjab National Bank"];
const RZP_METHOD = { upi: "upi", card: "card", netbanking: "netbanking", wallet: "wallet", paylater: "paylater" };

export default function CheckoutPayment() {
  const { bookingId } = useParams();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [cfg, setCfg] = useState({ enabled: false });
  const [sel, setSel] = useState("upi");
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/bookings").then((r) => setBooking(r.data.find((x) => x.id === bookingId) || false));
    api.get("/payments/config").then((r) => setCfg(r.data)).catch(() => {});
  }, [bookingId]);

  const finalize = async (payload) => {
    const { data } = await api.post("/payments/verify", { booking_id: bookingId, method: sel, ...payload });
    setDone(data.invoice);
    toast.success("Payment successful");
  };

  const pay = async () => {
    setProcessing(true);
    try {
      const { data: order } = await api.post("/payments/create-order", { booking_id: bookingId });
      if (cfg.enabled && !order.mock) {
        const ok = await loadRazorpay();
        if (!ok) throw new Error("Could not load Razorpay");
        const rzp = new window.Razorpay({
          key: order.key_id, amount: order.amount, currency: order.currency,
          name: order.name, description: order.description, order_id: order.order_id,
          prefill: order.prefill, theme: { color: "#EC5E3F" }, method: { [RZP_METHOD[sel]]: true },
          handler: async (resp) => {
            try { await finalize({ razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature }); }
            catch (e) { toast.error(apiErr(e)); } finally { setProcessing(false); }
          },
          modal: { ondismiss: () => setProcessing(false) },
        });
        rzp.open();
      } else {
        await new Promise((r) => setTimeout(r, 1300)); // simulate gateway
        await finalize({});
        setProcessing(false);
      }
    } catch (e) { toast.error(apiErr(e)); setProcessing(false); }
  };

  if (booking === false) return <Shell><div className="bmc-wrap py-24 text-center">Booking not found. <Link to="/seeker" className="text-coral">Back</Link></div></Shell>;
  if (!booking) return <Shell><div className="bmc-wrap py-24 text-center text-ink-soft">Loading…</div></Shell>;
  if (done) return <Success invoice={done} booking={booking} nav={nav} />;

  return (
    <Shell>
      <div className="bmc-wrap py-6">
        <button onClick={() => nav(`/checkout/${bookingId}`)} className="flex items-center gap-2 font-bold text-lg mb-6" data-testid="pay-back"><ArrowLeft size={20} /> Payment Options</button>

        {!cfg.enabled && (
          <div className="bg-butter-soft text-[#9A7320] text-sm rounded-r px-4 py-2.5 mb-5" data-testid="demo-mode-banner">
            ⚙️ Demo mode — payments are simulated (no real charge). Add Razorpay keys to go live.
          </div>
        )}

        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="card-bmc overflow-hidden grid sm:grid-cols-[240px_1fr]">
            {/* method rail */}
            <div className="border-r border-line-2 bg-ivory-2/40">
              {METHODS.map((m) => {
                const on = sel === m.key; const Icon = m.icon;
                return (
                  <button key={m.key} onClick={() => setSel(m.key)} data-testid={`method-${m.key}`}
                    className={`w-full text-left flex gap-3 px-4 py-4 border-b border-line-2 relative ${on ? "bg-card" : "hover:bg-card/60"}`}>
                    {on && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-coral" />}
                    <Icon size={20} className={on ? "text-coral" : "text-ink-soft"} />
                    <span>
                      <span className="flex items-center gap-2 font-semibold text-sm text-ink">{m.label}{m.badge && <span className="text-[10px] text-sage-deep font-bold">{m.badge}</span>}</span>
                      <span className="block text-xs text-ink-soft mt-0.5">{m.sub}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            {/* panel */}
            <div className="p-6 min-h-[320px]">
              {sel === "upi" && (
                <div>
                  <h3 className="font-bold mb-4">Pay by QR</h3>
                  <div className="flex flex-wrap items-center gap-6">
                    <div>
                      <p className="text-sm text-ink-soft mb-3">Scan the QR using any UPI app</p>
                      <div className="flex gap-2 text-ink-faint text-xs">{["Paytm", "PhonePe", "GPay", "BHIM"].map((a) => <span key={a} className="border border-line rounded px-2 py-1">{a}</span>)}</div>
                    </div>
                    <div className="grid place-items-center w-[160px] h-[160px] rounded-r border-2 border-dashed border-line bg-ivory-2 text-ink-faint">
                      <QrCode size={64} /><span className="text-xs mt-2">Scan to pay</span>
                    </div>
                  </div>
                  <div className="mt-6 max-w-xs">
                    <label className="text-xs text-ink-soft">Or enter UPI ID</label>
                    <input placeholder="yourname@bank" className="w-full border border-line rounded-r px-3 py-2.5 mt-1 text-sm bg-ivory focus:outline-none focus:border-coral" data-testid="upi-id" />
                  </div>
                </div>
              )}
              {sel === "card" && (
                <div className="max-w-sm">
                  <h3 className="font-bold mb-4">Card details</h3>
                  <input placeholder="Card number" className="inp3" data-testid="card-number" />
                  <div className="grid grid-cols-2 gap-3 mt-3"><input placeholder="MM / YY" className="inp3" /><input placeholder="CVV" className="inp3" /></div>
                  <input placeholder="Name on card" className="inp3 mt-3" />
                  <style>{`.inp3{width:100%;border:1px solid var(--line);border-radius:12px;padding:10px 12px;background:var(--ivory);font-size:14px;outline:none}.inp3:focus{border-color:var(--coral)}`}</style>
                </div>
              )}
              {sel === "netbanking" && (
                <div>
                  <h3 className="font-bold mb-4">Choose your bank</h3>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {BANKS.map((b) => <button key={b} className="text-left text-sm border border-line rounded-r px-4 py-3 hover:border-coral flex items-center gap-2"><Landmark size={15} className="text-ink-soft" /> {b}</button>)}
                  </div>
                </div>
              )}
              {sel === "wallet" && (
                <div>
                  <h3 className="font-bold mb-4">Select wallet</h3>
                  <div className="grid sm:grid-cols-2 gap-2">{["Amazon Pay", "PhonePe Wallet", "Mobikwik", "Freecharge"].map((w) => <button key={w} className="text-left text-sm border border-line rounded-r px-4 py-3 hover:border-coral">{w}</button>)}</div>
                </div>
              )}
              {sel === "paylater" && (
                <div>
                  <h3 className="font-bold mb-4">Pay Later</h3>
                  <div className="grid sm:grid-cols-2 gap-2">{["LazyPay", "Simpl", "ICICI PayLater"].map((w) => <button key={w} className="text-left text-sm border border-line rounded-r px-4 py-3 hover:border-coral">{w}</button>)}</div>
                </div>
              )}
            </div>
          </div>

          {/* totals */}
          <div className="space-y-4 self-start">
            <div className="card-bmc p-5">
              <h3 className="font-bold mb-3">Total Charges</h3>
              <div className="flex justify-between text-sm mb-1"><span className="text-ink-soft">Amount (incl. GST)</span><span>{rupee(booking.amount)}</span></div>
              <div className="flex justify-between text-sm mb-3"><span className="text-ink-soft">GST</span><span className="text-ink-faint">Included</span></div>
              <div className="flex justify-between font-bold pt-3 border-t border-line-2"><span>To Pay</span><span className="text-lg">{rupee(booking.amount)}</span></div>
              <button onClick={pay} disabled={processing} className="btn-pill bg-coral text-white w-full justify-center py-3 mt-4 disabled:opacity-60" data-testid="pay-now">
                {processing ? "Processing…" : `Pay ${rupee(booking.amount)}`}
              </button>
            </div>
            <p className="flex items-center justify-center gap-2 text-xs text-ink-faint"><ShieldCheck size={14} className="text-sage" /> 100% Secured Payments · Powered by Razorpay</p>
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Success({ invoice, booking, nav }) {
  return (
    <Shell>
      <div className="bmc-wrap py-12 max-w-lg mx-auto">
        <div className="card-bmc p-8 text-center print:shadow-none" id="receipt">
          <CheckCircle2 size={64} className="text-sage mx-auto" />
          <h1 className="text-2xl font-bold mt-4">Payment successful</h1>
          <p className="text-ink-soft mt-1">Your booking with {booking.carer_name} is confirmed.</p>
          <div className="text-left bg-ivory-2 rounded-r p-5 mt-6 text-sm space-y-2">
            <Row l="Invoice" v={invoice.id} />
            <Row l="Booking" v={booking.id} />
            <Row l="Service" v={invoice.service} />
            <Row l="Method" v={{ upi: "UPI", card: "Card", netbanking: "Net Banking", wallet: "Wallet", paylater: "Pay Later" }[invoice.method] || (invoice.method || "").toUpperCase()} />
            <div className="border-t border-line my-2" />
            <Row l="Taxable value" v={rupee(invoice.taxable_value)} />
            <Row l={`CGST (9%)`} v={rupee(invoice.cgst)} />
            <Row l={`SGST (9%)`} v={rupee(invoice.sgst)} />
            <Row l="SAC code" v={invoice.hsn_sac} />
            <div className="border-t border-line my-2" />
            <Row l="Total paid" v={rupee(invoice.amount)} bold />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-6 print:hidden">
            <button onClick={() => window.print()} className="btn-pill border border-line justify-center py-3" data-testid="download-receipt"><Download size={16} /> Receipt</button>
            <button onClick={() => nav("/seeker")} className="btn-pill bg-coral text-white justify-center py-3" data-testid="go-bookings">Go to Bookings</button>
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Row({ l, v, bold }) {
  return <div className="flex justify-between"><span className="text-ink-soft">{l}</span><span className={bold ? "font-bold text-base" : "font-medium"}>{v}</span></div>;
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-ivory">
      <div className="h-[64px] bg-card border-b border-line flex items-center"><div className="bmc-wrap"><Logo /></div></div>
      {children}
    </div>
  );
}
